/**
 * Shared sort types for view transforms
 * Reusable across all transforms that need sorting functionality
 */

export interface SortCriteria {
  column: string;
  direction: "asc" | "desc";
}

/**
 * Three-state sort toggle: desc -> asc -> remove
 *
 * **Single mode** (default): replaces any existing sort with the clicked column.
 * **Multi mode** (shift-click): adds/toggles the column alongside existing sorts.
 */
export function getNextSortState(
  currentSort: SortCriteria[],
  column: string,
  multi = false,
): SortCriteria[] {
  const existingIndex = currentSort.findIndex((s) => s.column === column);
  const existing = existingIndex >= 0 ? currentSort[existingIndex] : null;

  if (!existing) {
    if (multi) {
      // Shift-click: append new column
      return [...currentSort, { column, direction: "desc" }];
    }
    // Single click: replace with just this column
    return [{ column, direction: "desc" }];
  } else if (existing.direction === "desc") {
    // Currently desc: change to asc
    const newSort = multi ? [...currentSort] : [existing];
    newSort[multi ? existingIndex : 0] = { column, direction: "asc" };
    return newSort;
  } else {
    // Currently asc: remove from sort
    if (multi) {
      return currentSort.filter((_, index) => index !== existingIndex);
    }
    return [];
  }
}

/**
 * Get the current sort direction for a column
 */
export function getSortDirection(sort: SortCriteria[], column: string): "asc" | "desc" | null {
  const criteria = sort.find((s) => s.column === column);
  return criteria?.direction ?? null;
}

/**
 * Get the 1-based position of a column in the sort list, or -1 if not sorted.
 */
export function getSortIndex(sort: SortCriteria[], column: string): number {
  const idx = sort.findIndex((s) => s.column === column);
  return idx >= 0 ? idx + 1 : -1;
}
